import React, { useState, useEffect, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Typography,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Chip,
  Paper,
  Divider,
  LinearProgress,
  Tooltip,
} from '@mui/material';
import { 
  CheckCircle as CheckCircleIcon, 
  Error as ErrorIcon, 
  HourglassEmpty as HourglassEmptyIcon,
  Schedule as ScheduleIcon
} from '@mui/icons-material';
import { getIngestionProgressLog } from "../../services/apiService";

const PIPELINE_STEPS = [
  { id: 'ingestion_pipeline_start', label: 'Start Ingestion Pipeline' },
  { id: 'check_file_exists', label: 'Check File Exists' },
  { id: 'pdf_to_images', label: 'Convert PDF to Images' },
  { id: 'vlm_processing', label: 'Vision Language Model Processing' },
  { id: 'embedding_generation', label: 'Generate Embeddings' },
  { id: 'save_processed_data', label: 'Save Processed Data' },
  { id: 'content_extraction', label: 'Extract Content' },
  { id: 'summary_generation', label: 'Generate Summary' },
  { id: 'summary_upload_to_delta', label: 'Upload Summary' },
  { id: 'metadata_extraction_from_summary', label: 'Extract Metadata from Summary' },
  { id: 'embedding_upload_to_delta', label: 'Upload Embeddings' },
  { id: 'direct_vector_index_upload', label: 'Direct Vector Index Upload' },
  { id: 'verify_index_ingestion', label: 'Verify Index Ingestion' },
  { id: 'ingestion_pipeline_completed', label: 'Complete Ingestion Pipeline' }
];

const FileProcessingProgress = ({ fileName, pipelineStatus }) => {
  const [processedSteps, setProcessedSteps] = useState({});
  const [overallStatus, setOverallStatus] = useState('in_progress');
  const [activeStep, setActiveStep] = useState(0);
  const [totalDuration, setTotalDuration] = useState(0);
  const [runId, setRunId] = useState(null);
  const pollingIntervalRef = useRef(null);

  // Extract run_id from initial pipeline status if available
  useEffect(() => {
    if (Array.isArray(pipelineStatus) && pipelineStatus.length > 0) {
      const runIdFromStatus = pipelineStatus[0]?.run_id;
      if (runIdFromStatus) {
        setRunId(runIdFromStatus);
      }
    } else if (pipelineStatus && typeof pipelineStatus === 'object') {
      // For object format responses
      setRunId(pipelineStatus.run_id || null);
    }
  }, [pipelineStatus]);

  // Helper function to initialize step data
  const initializeStepData = (sequenceNumber = 0) => ({
    status: 'not_started',
    message: '',
    executionTime: 0,
    startTime: null,
    endTime: null,
    sequenceNumber
  });

  // Helper function to update step data
  const updateStepData = (currentData, newData) => ({
    ...currentData,
    status: newData.status,
    message: newData.message || '',
    executionTime: parseFloat(newData.executionTime) || 0,
    startTime: newData.startTimeStr ? new Date(newData.startTimeStr) : null,
    endTime: newData.endTimeStr ? new Date(newData.endTimeStr) : null,
    sequenceNumber: newData.sequenceNumber || 0
  });

  // Helper function to process object format entry
  const processObjectEntry = useCallback((entry, processedData, currentFileName) => {
    const { 
      filename: file,
      step,
      status,
      message,
      execution_time: executionTime,
      start_time: startTimeStr,
      end_time: endTimeStr,
      sequence_number: sequenceNumber 
    } = entry;
    
    if (file !== currentFileName) return { shouldContinue: false };
    
    if (!processedData[step]) {
      processedData[step] = initializeStepData(sequenceNumber || 0);
    }
    
    processedData[step] = updateStepData(processedData[step], {
      status,
      message,
      executionTime,
      startTimeStr,
      endTimeStr,
      sequenceNumber
    });
    
    return { 
      shouldContinue: true,
      isPipelineCompleted: step === 'ingestion_pipeline' && status === 'completed',
      executionTime: parseFloat(executionTime) || 0
    };
  }, []);

  // Helper function to process array format entry
  const processArrayEntry = useCallback((entry, processedData, currentFileName) => {
    const [, file, step, status, message, executionTime, startTimeStr, endTimeStr, sequenceNumber] = entry;
    
    if (file !== currentFileName) return { shouldContinue: false };
    
    if (!processedData[step]) {
      processedData[step] = initializeStepData(sequenceNumber);
    }
    
    processedData[step] = updateStepData(processedData[step], {
      status,
      message,
      executionTime,
      startTimeStr,
      endTimeStr,
      sequenceNumber
    });
    
    return { 
      shouldContinue: true,
      isPipelineCompleted: step === 'ingestion_pipeline' && status === 'completed',
      executionTime: parseFloat(executionTime) || 0
    };
  }, []);

  // Helper function to determine active step
  const determineActiveStep = (processedData, pipelineSteps) => {
    let currentActiveStep = 0;
    
    pipelineSteps.forEach((step, index) => {
      const stepData = processedData[step.id] || processedData[step.id === 'ingestion_pipeline_start' ? 'ingestion_pipeline' : step.id];
      
      if (stepData) {
        if (stepData.status === 'started' && index > currentActiveStep) {
          currentActiveStep = index;
        } else if (stepData.status === 'completed' && index >= currentActiveStep) {
          currentActiveStep = index + 1;
        }
      }
    });
    
    return Math.min(currentActiveStep, pipelineSteps.length - 1);
  };

  // Helper function to determine overall status
  const determineOverallStatus = (processedData) => {
    const isCompleted = processedData['ingestion_pipeline']?.status === 'completed' || 
                         processedData['ingestion_pipeline_completed']?.status === 'completed';
    const hasFailed = Object.values(processedData).some(step => step.status === 'failed');
    
    let overallStatus;
    if (isCompleted) {
      overallStatus = 'completed';
    } else if (hasFailed) {
      overallStatus = 'failed';
    } else {
      overallStatus = 'in_progress';
    }
    
    return overallStatus;
  };

  const processPipelineStatusArray = useCallback((statusArray) => {
    if (!statusArray || statusArray.length === 0) return;
    
    const processedData = {};
    let duration = 0;
    
    // Process each entry in the array
    statusArray.forEach(entry => {
      let result;
      
      // Process object format entry
      if (typeof entry === 'object' && !Array.isArray(entry)) {
        result = processObjectEntry(entry, processedData, fileName);
      }
      // Process array format entry
      else if (Array.isArray(entry) && entry.length >= 9) {
        result = processArrayEntry(entry, processedData, fileName);
      }
      // Handle invalid format
      else {
        console.warn('Invalid pipeline status entry format:', entry);
        return;
      }
      
      // Update duration if pipeline is completed
      if (result?.isPipelineCompleted) {
        duration = result.executionTime;
      }
    });
    
    // Update state with processed data
    setTotalDuration(duration);
    setProcessedSteps(processedData);
    setActiveStep(determineActiveStep(processedData, PIPELINE_STEPS));
    setOverallStatus(determineOverallStatus(processedData));
  }, [fileName, processArrayEntry, processObjectEntry]);

  // Set up polling for progress updates
  useEffect(() => {
    // Process initial data
    if (Array.isArray(pipelineStatus)) {
      processPipelineStatusArray(pipelineStatus);
    } else {
      // Handle the case where pipelineStatus is an object (legacy format)
      const processedData = {};
      let currentActiveStep = 0;

      PIPELINE_STEPS.forEach((step, index) => {
        const stepId = step.id;
        const status = pipelineStatus[stepId] || 'not_started';

        processedData[stepId] = {
          status,
          message: '',
          executionTime: 0,
          startTime: null,
          endTime: null,
          sequenceNumber: index
        };

        if (status === 'started' && index > currentActiveStep) {
          currentActiveStep = index;
        } else if (status === 'completed' && index >= currentActiveStep) {
          currentActiveStep = index + 1;
        }
      });

      setProcessedSteps(processedData);
      setActiveStep(Math.min(currentActiveStep, PIPELINE_STEPS.length - 1));

      const isCompleted = processedData['ingestion_pipeline_completed']?.status === 'completed';
      const hasFailed = Object.values(processedData).some(step => step.status === 'failed');

      let overallStatus;
      if (isCompleted) {
        overallStatus = 'completed';
      } else if (hasFailed) {
        overallStatus = 'failed';
      } else {
        overallStatus = 'in_progress';
      }

      setOverallStatus(overallStatus);
    }

    if (fileName && runId) {
      const pollProgressStatus = async () => {
        try {
          const response = await getIngestionProgressLog(fileName, runId);
          console.log("Polled ingestion progress:", response);
          if (response?.data && Array.isArray(response.data)) {
            processPipelineStatusArray(response.data);
          } else if (response?.status) {
            const legacyData = response.data || {};
            const processedData = {};
            let currentActiveStep = 0;

            PIPELINE_STEPS.forEach((step, index) => {
              const stepId = step.id;
              const status = legacyData[stepId] || 'not_started';

              processedData[stepId] = {
                status,
                message: '',
                executionTime: 0,
                startTime: null,
                endTime: null,
                sequenceNumber: index
              };

              if (status === 'started' && index > currentActiveStep) {
                currentActiveStep = index;
              } else if (status === 'completed' && index >= currentActiveStep) {
                currentActiveStep = index + 1;
              }
            });

            setProcessedSteps(processedData);
            setActiveStep(Math.min(currentActiveStep, PIPELINE_STEPS.length - 1));

            const isCompleted = processedData['ingestion_pipeline_completed']?.status === 'completed';
            const hasFailed = Object.values(processedData).some(step => step.status === 'failed');

            let nextOverallStatus;
            if (isCompleted) {
              nextOverallStatus = 'completed';
            } else if (hasFailed) {
              nextOverallStatus = 'failed';
            } else {
              nextOverallStatus = 'in_progress';
            }

            setOverallStatus(nextOverallStatus);
          }

          if (overallStatus === 'completed' || overallStatus === 'failed') {
            if (pollingIntervalRef.current) {
              clearInterval(pollingIntervalRef.current);
              pollingIntervalRef.current = null;
            }
          }
        } catch (error) {
          console.error("Error polling ingestion progress:", error);
        }
      };

      if (!pollingIntervalRef.current) {
        pollingIntervalRef.current = setInterval(pollProgressStatus, 30000);
      }

      return () => {
        if (pollingIntervalRef.current) {
          clearInterval(pollingIntervalRef.current);
          pollingIntervalRef.current = null;
        }
      };
    }
  }, [fileName, runId, pipelineStatus, overallStatus, processPipelineStatusArray]);

  const getStepStatus = (stepId) => {
    // Check for both variations of step ID
    const step = processedSteps[stepId] || 
                (stepId === 'ingestion_pipeline_start' && processedSteps['ingestion_pipeline'] && 
                 processedSteps['ingestion_pipeline'].status === 'started' ? 
                   { status: 'completed' } : null) || 
                (stepId === 'ingestion_pipeline_completed' && processedSteps['ingestion_pipeline'] && 
                 processedSteps['ingestion_pipeline'].status === 'completed' ? 
                   { status: 'completed' } : null);
    
    return step?.status || 'not_started';
  };

  const getStepMessage = (stepId) => {
    // Check for both variations of step ID
    const step = processedSteps[stepId] || 
                (stepId === 'ingestion_pipeline_start' && processedSteps['ingestion_pipeline']) || 
                (stepId === 'ingestion_pipeline_completed' && processedSteps['ingestion_pipeline']);
    
    return step?.message || '';
  };

  const getStepExecutionTime = (stepId) => {
    const step = processedSteps[stepId] || 
                (stepId === 'ingestion_pipeline_start' && processedSteps['ingestion_pipeline']) || 
                (stepId === 'ingestion_pipeline_completed' && processedSteps['ingestion_pipeline']);
    
    return step?.executionTime || 0;
  };

  const formatTime = (date) => {
    if (!date || !(date instanceof Date) || isNaN(date.getTime())) {
      return 'Unknown';
    }
    
    return date.toTimeString().split(' ')[0]; // Returns "HH:MM:SS" format
  };

  // Helper function to get status label
  const getStatusLabel = (status) => {
    if (status === 'completed') return 'Completed';
    if (status === 'failed') return 'Failed';
    return 'In Progress';
  };

  const getStepTiming = (stepId) => {
    const step = processedSteps[stepId] || 
                (stepId === 'ingestion_pipeline_start' && processedSteps?.['ingestion_pipeline']) || 
                (stepId === 'ingestion_pipeline_completed' && processedSteps?.['ingestion_pipeline']);
    
    if (!step || !step.startTime) return null;
    
    const startTime = step.startTime instanceof Date ? step.startTime : new Date(step.startTime);
    
    let endTime;
    if (step.endTime instanceof Date) {
      endTime = step.endTime;
    } else if (step.endTime) {
      endTime = new Date(step.endTime);
    } else {
      endTime = null;
    }
    
    return {
      startTime,
      endTime,
      formatted: {
        startTime: isNaN(startTime.getTime()) ? 'Unknown' : formatTime(startTime),
        endTime: endTime && !isNaN(endTime.getTime()) ? formatTime(endTime) : 'In progress'
      }
    };
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <CheckCircleIcon sx={{ color: 'success.main' }} />;
      case 'failed':
        return <ErrorIcon sx={{ color: 'error.main' }} />;
      case 'started':
        return <HourglassEmptyIcon sx={{ color: 'info.main' }} />;
      default:
        return <ScheduleIcon sx={{ color: 'text.secondary' }} />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'success';
      case 'failed':
        return 'error';
      case 'started':
        return 'info';
      default:
        return 'default';
    }
  };

  const formatDuration = (seconds) => {
    if (!seconds || isNaN(seconds)) return '0s';
    
    if (seconds < 60) {
      return `${seconds.toFixed(1)}s`;
    }
    
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = (seconds % 60).toFixed(1);
    
    return `${minutes}m ${remainingSeconds}s`;
  };

  return (
    <Paper elevation={0} sx={{ p: 2, bgcolor: 'background.paper', borderRadius: 2 }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" component="h3" sx={{ fontFamily: "'Poppins', sans-serif", mb: 0.5 }}>
          {fileName}
        </Typography>
        
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          <Chip 
            label={getStatusLabel(overallStatus)} 
            color={getStatusColor(overallStatus === 'in_progress' ? 'started' : overallStatus)}
            size="small" 
            sx={{ mr: 2 }}
          />
          {totalDuration > 0 && (
            <Tooltip title="Total processing time">
              <Typography variant="body2" color="text.secondary">
                <ScheduleIcon fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.5 }} />
                {formatDuration(totalDuration)}
              </Typography>
            </Tooltip>
          )}
        </Box>
        
        <Box sx={{ my: 2 }}>
          <LinearProgress 
            variant="determinate"
            value={(activeStep / (PIPELINE_STEPS.length - 1)) * 100}
            sx={{ height: 8, borderRadius: 4 }}
            color={overallStatus === 'failed' ? 'error' : 'primary'}
          />
        </Box>
      </Box>

      <Divider sx={{ my: 2 }} />

      <Stepper activeStep={activeStep} orientation="vertical">
        {PIPELINE_STEPS.map((step) => {
          const stepStatus = getStepStatus(step.id);
          const stepMessage = getStepMessage(step.id);
          const executionTime = getStepExecutionTime(step.id);
          const timing = getStepTiming(step.id);

          return (
            <Step key={step.id} expanded={true}>
              <StepLabel 
                StepIconComponent={() => getStatusIcon(stepStatus)}
                sx={{ '& .MuiStepLabel-label': { fontFamily: "'Poppins', sans-serif" } }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                  <Typography 
                    variant="body1" 
                    sx={{ fontWeight: activeStep === PIPELINE_STEPS.indexOf(step) ? 600 : 400 }}
                  >
                    {step.label}
                  </Typography>
                  {executionTime > 0 && (
                    <Chip 
                      size="small" 
                      label={formatDuration(executionTime)} 
                      variant="outlined" 
                      sx={{ ml: 1, fontSize: '0.7rem' }}
                    />
                  )}
                </Box>
              </StepLabel>
              <StepContent>
                <Box sx={{ mb: 2, mt: 1 }}>
                  {stepMessage && (
                    <Typography 
                      variant="body2" 
                      color="text.secondary" 
                      sx={{ mb: 1, fontFamily: "'Poppins', sans-serif",wordWrap: 'break-word', overflowWrap: 'anywhere' }}
                    >
                      {stepMessage}
                    </Typography>
                  )}
                  
                  {timing && (
                    <Box sx={{ display: 'flex', alignItems: 'center', fontSize: '0.75rem', color: 'text.secondary' }}>
                      <ScheduleIcon fontSize="small" sx={{ mr: 0.5 }} />
                      <Typography variant="caption">
                        {timing.formatted.startTime} - {timing.formatted.endTime}
                      </Typography>
                    </Box>
                  )}
                </Box>
              </StepContent>
            </Step>
          );
        })}
      </Stepper>
    </Paper>
  );
};

FileProcessingProgress.propTypes = {
  fileName: PropTypes.string.isRequired,
  pipelineStatus: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.shape({
      run_id: PropTypes.string,
    })),
    PropTypes.shape({
      run_id: PropTypes.string,
    })
  ])
};

export default FileProcessingProgress;
